<template>
  <div class="scale-selector">
    <select
      v-model="localSelectedScale"
      @change="updateScale"
      class="scale-select interactive-outline"
    >
      <template v-for="item in organizedScales" :key="item.key">
        <option v-if="item.isSeparator" disabled>─────</option>
        <option v-else :value="item.value">{{ item.name }}</option>
      </template>
    </select>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { storeToRefs } from "pinia";
import { useFretboardStore } from "@/stores/fretboard";
import { useScales } from "@/composables/useScales";

const store = useFretboardStore();
const { selectedScaleName } = storeToRefs(store);
const { scales } = useScales();

const localSelectedScale = ref(selectedScaleName.value);

// Organize scales with line separators
const organizedScales = computed(() => {
  return [
    // Chromatic
    {
      key: "chromatic",
      value: "chromatic",
      name: scales.value.chromatic.name,
      isSeparator: false,
    },

    // Separator
    { key: "sep1", isSeparator: true },

    // Basic Scales
    {
      key: "major",
      value: "major",
      name: scales.value.major.name,
      isSeparator: false,
    },
    {
      key: "minor",
      value: "minor",
      name: scales.value.minor.name,
      isSeparator: false,
    },

    // Separator
    { key: "sep2", isSeparator: true },

    // Modal Scales
    {
      key: "ionian",
      value: "ionian",
      name: scales.value.ionian.name,
      isSeparator: false,
    },
    {
      key: "dorian",
      value: "dorian",
      name: scales.value.dorian.name,
      isSeparator: false,
    },
    {
      key: "phrygian",
      value: "phrygian",
      name: scales.value.phrygian.name,
      isSeparator: false,
    },
    {
      key: "lydian",
      value: "lydian",
      name: scales.value.lydian.name,
      isSeparator: false,
    },
    {
      key: "mixolydian",
      value: "mixolydian",
      name: scales.value.mixolydian.name,
      isSeparator: false,
    },
    {
      key: "aeolian",
      value: "aeolian",
      name: scales.value.aeolian.name,
      isSeparator: false,
    },
    {
      key: "locrian",
      value: "locrian",
      name: scales.value.locrian.name,
      isSeparator: false,
    },

    // Separator
    { key: "sep3", isSeparator: true },

    // Pentatonic & Blues
    {
      key: "major-pentatonic",
      value: "major pentatonic",
      name: scales.value["major pentatonic"].name,
      isSeparator: false,
    },
    {
      key: "minor-pentatonic",
      value: "minor pentatonic",
      name: scales.value["minor pentatonic"].name,
      isSeparator: false,
    },
    {
      key: "blues",
      value: "blues",
      name: scales.value.blues.name,
      isSeparator: false,
    },

    // Separator
    { key: "sep4", isSeparator: true },

    // Custom
    {
      key: "custom",
      value: "custom",
      name: scales.value.custom.name,
      isSeparator: false,
    },
  ];
});

watch(selectedScaleName, (newValue) => {
  localSelectedScale.value = newValue;
});

function updateScale() {
  store.setSelectedScale(localSelectedScale.value);
}
</script>

<style scoped lang="scss">
.scale-select {
  /* Scale Selector */
  max-width: 120px;
  height: 44px;

  background: var(--shade-10);
  border: none;
  border-radius: 32px;

  font-weight: 400;
  font-size: 14px;
  text-align: left;
  padding: 0 32px 0 16px;

  color: var(--shade-70);

  flex: none;
  order: 1;
  flex-grow: 0;

  cursor: pointer;
  transition: all 0.36s ease-out, outline 0.1s ease-out;

  appearance: none; /* Remove default arrow */
  background-image: url('data:image/svg+xml;charset=US-ASCII,<svg width="8" height="5" viewBox="0 0 8 5" fill="%23333" xmlns="http://www.w3.org/2000/svg"><path d="M0.097818 1.06235C-0.111142 0.853387 0.0368522 0.496094 0.332367 0.496094H7.02335C7.31886 0.496094 7.46686 0.853387 7.2579 1.06235L3.91241 4.40787C3.78287 4.53741 3.57285 4.53741 3.44331 4.40787L0.097818 1.06235Z" fill="white"/></svg>');
  background-repeat: no-repeat;
  background-position: right 12px center; /* Adjust the '12px' to move arrow left/right */
  background-size: 12px;
}
</style>
